import { useEffect, useState } from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import { useFinance, usePreferences } from '../../../contexts';
import { getScaleColor } from '../colorScale';
import styles from './DistribuicaoDespesas.module.css';

const MAX_VISIBLE = 6;

interface TooltipProps {
    active?: boolean;
    payload?: Array<{ name: string; value: number; payload: { percentual: number } }>;
    formatCurrency: (value: number) => string;
}

const CustomTooltip = ({ active, payload, formatCurrency }: TooltipProps) => {
    if (active && payload?.length) {
        const item = payload[0];
        return (
            <div className={styles.tooltip}>
                <p className={styles.tooltipLabel}>{item.name}</p>
                <p>
                    {formatCurrency(item.value)}
                    {' '}({item.payload.percentual}%)
                </p>
            </div>
        );
    }
    return null;
};

function groupData(
    items: Array<{ categoria: string; valor: number; percentual: number }>,
    labelOutros: string,
) {
    const maxVal = Math.max(...items.map(i => i.valor), 1);
    if (items.length <= MAX_VISIBLE) {
        return items.map((item) => ({
            name: item.categoria,
            value: item.valor,
            percentual: item.percentual,
            fill: getScaleColor(item.valor, maxVal, 'expense'),
        }));
    }
    const sorted = [...items].sort((a, b) => b.valor - a.valor);
    const top = sorted.slice(0, MAX_VISIBLE - 1);
    const rest = sorted.slice(MAX_VISIBLE - 1);
    const outrosValor = rest.reduce((s, r) => s + r.valor, 0);
    const outrosPerc = rest.reduce((s, r) => s + r.percentual, 0);
    const result = top.map((item) => ({
        name: item.categoria,
        value: item.valor,
        percentual: item.percentual,
        fill: getScaleColor(item.valor, maxVal, 'expense'),
    }));
    result.push({
        name: `${labelOutros} (${rest.length})`,
        value: outrosValor,
        percentual: Math.round(outrosPerc * 100) / 100,
        fill: '#6e1010',
    });
    return result;
}

export function DistribuicaoDespesas() {
    const { distribuicaoDespesas } = useFinance();
    const [showAll, setShowAll] = useState(false);
    const [isSmall, setIsSmall] = useState(false);
    const { formatCurrency, t } = usePreferences();

    useEffect(() => {
        const mql = window.matchMedia('(max-width: 480px)');
        setIsSmall(mql.matches);
        const handler = (e: MediaQueryListEvent) => setIsSmall(e.matches);
        mql.addEventListener('change', handler);
        return () => mql.removeEventListener('change', handler);
    }, []);

    const maxVal = Math.max(...distribuicaoDespesas.map(d => d.valor), 1);

    const allData = distribuicaoDespesas.map((item) => ({
        name: t(item.categoria),
        value: item.valor,
        percentual: item.percentual,
        fill: getScaleColor(item.valor, maxVal, 'expense'),
    }));

    const chartData = groupData(
        distribuicaoDespesas.map(item => ({ ...item, categoria: t(item.categoria) })),
        t('chart.outros')
    );
    const legendItems = showAll ? allData : chartData;
    const hasMore = distribuicaoDespesas.length > MAX_VISIBLE;

    return (
        <div className={styles.container}>
            <h3 className={styles.title}>{t('chart.distribuicaoDespesas')}</h3>
            <span className={styles.subtitle}>{t('chart.porCategoria')}</span>

            <div className={styles.chartContainer}>
                {chartData.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                            <Pie
                                data={chartData}
                                dataKey="value"
                                nameKey="name"
                                cx="50%"
                                cy="50%"
                                outerRadius={isSmall ? 65 : 90}
                                paddingAngle={2}
                                animationDuration={1500}
                            >
                                {chartData.map((entry, index) => (
                                    <Cell key={index} fill={entry.fill} />
                                ))}
                            </Pie>
                            <Tooltip content={<CustomTooltip formatCurrency={formatCurrency} />} />
                        </PieChart>
                    </ResponsiveContainer>
                ) : (
                    <div className={styles.empty}>{t('common.semDados')}</div>
                )}
            </div>

            {chartData.length > 0 && (
                <div className={styles.legend}>
                    {legendItems.map((item, i) => (
                        <div key={i} className={styles.legendItem}>
                            <span className={styles.legendDot} style={{ backgroundColor: item.fill }} />
                            <span className={styles.legendLabel}>{item.name}</span>
                            <span className={styles.legendValue}>{item.percentual}%</span>
                        </div>
                    ))}
                    {hasMore && (
                        <button
                            className={styles.showMoreBtn}
                            onClick={() => setShowAll((v) => !v)}
                        >
                            {showAll ? t('chart.verMenos') : `${t('chart.verTodas')} (${distribuicaoDespesas.length})`}
                        </button>
                    )}
                </div>
            )}
        </div>
    );
}
